import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PlaylistVideo } from '@/store/playlist/types';
import { TemplatePandaPreview } from '@/components/playlist/preview/TemplatePandaPreview';
import { TemplatePrimeVideoPreview } from '@/components/playlist/preview/TemplatePrimeVideoPreview';
import { TemplateCleanPreview } from '@/components/playlist/preview/TemplateCleanPreview';
import { TemplateDarkMembersPreview } from '@/components/playlist/preview/TemplateDarkMembersPreview';
import { Play, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePlaylistStore } from '@/store/playlist/store';
// TODO: Implement API call to fetch dark members settings
// import { mockDarkMembersSettings } from './mockDarkMembersData';

const mockDarkMembersSettings: any = {
  modules: [],
  settings: {},
};

// Mock videos para preview
const mockVideos: PlaylistVideo[] = [
  { id: '1', videoId: 'v1', order: 0, episodeTitle: 'Introdução ao Curso' },
  { id: '2', videoId: 'v2', order: 1, episodeTitle: 'Fundamentos Essenciais' },
  { id: '3', videoId: 'v3', order: 2, episodeTitle: 'Conceitos Avançados' },
  { id: '4', videoId: 'v4', order: 3, episodeTitle: 'Prática Guiada' },
  { id: '5', videoId: 'v5', order: 4, episodeTitle: 'Estratégias de Aplicação' },
  { id: '6', videoId: 'v6', order: 5, episodeTitle: 'Estudos de Caso' },
  { id: '7', videoId: 'v7', order: 6, episodeTitle: 'Otimização de Resultados' },
  { id: '8', videoId: 'v8', order: 7, episodeTitle: 'Conclusão e Próximos Passos' },
];

const templateNames: Record<string, string> = {
  clean: 'Clean',
  panda: 'Light',
  netflix: 'Netflix Dark',
  'dark-members': 'Dark Members 2.0',
};

// Mock data importado de arquivo separado

export default function PlaylistTemplatePreview() {
  useLanguage();
  const { template, playlistId } = useParams<{ template: string; playlistId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const getPlaylistById = usePlaylistStore(state => state.getPlaylistById);

  // Buscar dados reais da playlist se existir playlistId
  const playlist = playlistId ? getPlaylistById(playlistId) : null;
  
  // Prioridade: Query params > Dados da playlist > Fallback
  const publicTitle = searchParams.get('title') || playlist?.publicTitle || undefined;
  const description = searchParams.get('description') || playlist?.description || undefined;
  const playlistName = searchParams.get('name') || playlist?.name || 'Curso de Exemplo';
  const favicon = searchParams.get('favicon') || playlist?.favicon || undefined;
  
  // Usar vídeos reais ou mock
  const videos = playlist?.videos || mockVideos;

  useEffect(() => {
    // Add noindex meta tag
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const renderTemplate = () => {
    switch (template) {
      case 'clean':
        return (
          <TemplateCleanPreview 
            videos={videos}
            playlistName={playlistName}
            publicTitle={publicTitle}
            description={description}
            favicon={favicon}
          />
        );
      case 'panda':
        return (
          <TemplatePandaPreview 
            videos={videos} 
            playlistName={playlistName}
            publicTitle={publicTitle}
            favicon={favicon}
            description={description}
          />
        );
      case 'netflix':
        return (
          <TemplatePrimeVideoPreview
            videos={videos}
            settings={playlist?.primeVideoSettings}
            playlistName={playlistName}
            publicTitle={publicTitle}
            description={description}
            favicon={favicon}
          />
        );
      case 'dark-members':
        return (
          <TemplateDarkMembersPreview 
            settings={mockDarkMembersSettings}
            onModuleClick={(moduleId) => navigate(`/playlist/preview/dark-members/module/${moduleId}`)}
            onBonusClick={(bonusId) => console.log('Bonus clicked:', bonusId)}
          />
        );
      default:
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <p className="text-lg text-gray-600">{translateInterfaceText("Template não encontrado")}</p>
          </div>
        );
    }
  };

  return renderTemplate();
}

